<script>
import { MODAL, RELOAD } from '@constants/Text'
import { GlobalBus } from '@services/bus'
import { apiConsolidatorEvents } from '@modules/consolidator/api/apiConsolidatorEvents'
import {
  normalizeEventList,
  zeroEventValues,
} from '@modules/consolidator/utils/Events/Events'
import MovimentacoesTable from '@modules/consolidator/components/movimentacoes-table/movimentacoes-table.vue'
import EventosTable from '@modules/consolidator/components/eventos-table/eventos-table.vue'
import MovementsEventsCombinedTable from '@modules/consolidator/components/movements-events-combined-table/movements-events-combined-table.vue'

const MODE_TITLES = {
  movements: 'Movimentações',
  events: 'Eventos',
  both: 'Movimentações e eventos',
}

const EVENT_TYPE = {
  PROVENTOS: 'proventos',
  SPLITS: 'splits',
}

export default {
  name: 'MovementsEventsTable',
  components: {
    MovimentacoesTable,
    EventosTable,
    MovementsEventsCombinedTable,
  },
  props: {
    mode: {
      type: String,
      default: 'movements',
      validator: (value) => ['movements', 'events', 'both'].includes(value),
    },
    asset: {
      type: Object,
      default: () => ({}),
    },
    sugestoesPorMovimentacao: {
      type: Object,
      default: null,
    },
    search: {
      type: String,
      default: '',
    },
    movementItems: {
      type: Array,
      default: () => [],
    },
    eventItems: {
      type: Array,
      default: () => [],
    },
    showAlertFillPu: {
      type: Boolean,
      default: false,
    },
    idAssetAlertFillPu: {
      type: [Number, String],
      default: null,
    },
    showAssetNameColumn: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    hideTitle: {
      type: Boolean,
      default: false,
    },
    bus: {
      type: Object,
      default: null,
    },
    client: {
      type: Object,
      default: null,
    },
    proventos: {
      type: [Object, Array],
      default: null,
    },
    splits: {
      type: [Object, Array],
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    resolvedTitle() {
      if (this.hideTitle) {
        return ''
      }
      return MODE_TITLES[this.mode] || ''
    },
  },
  methods: {
    onEditMovement(item) {
      if (!this.bus) {
        return
      }
      const asset = this.asset && this.asset.id ? this.asset : item
      const client =
        this.client || this.$store.getters['user/currentClient'] || {}
      this.bus.$emit(
        MODAL.CADASTRAR_APORTE,
        { id: client.id, name: client.nome_cliente },
        item,
        asset
      )
    },
    onRemoveMovement(item) {
      this.$emit('remove-movement', item)
      this.$emit('remove-movimentacao', item)
    },
    onEditEvent(item) {
      const eventos = this.resolveEventList(item)
      GlobalBus.$emit(MODAL.CONSOLIDATOR_ASSET_EVENT, {
        item: this.findOriginalEvent({ eventos, item }),
        ativo: this.asset,
        eventos,
        callback: () => {
          GlobalBus.$emit(RELOAD.CONSOLIDATOR_ASSET_EVENT, true)
        },
      })
    },
    onRemoveEvent(item) {
      this.$showModal({
        type: 'danger',
        v2: true,
        center: true,
        title: 'Remover evento',
        text: 'Tem certeza que deseja remover este evento? Essa ação não pode ser desfeita.',
        buttons: [
          { text: 'Cancelar', action: () => {} },
          { text: 'Remover', action: () => this.removeEvent(item) },
        ],
      })
    },
    /**
     * Chave e valor que identificam a linha a zerar, ou `null` quando a lista
     * nao tem essa linha.
     *
     * Duas condicoes, nao uma. O guard no VALOR (e nao na chave) existe porque
     * `key` e sempre `'_eventUid'` ou `'_id'`: quando `findOriginalEvent` cai no
     * fallback e a linha nao tem a chave, `original[key]` e `undefined` e TODA
     * linha sem ela casaria — zerando eventos que ninguem mandou remover. E ter
     * a chave preenchida ainda nao e ACHAR a linha: no mesmo fallback o `find`
     * falhou e devolveu o proprio `item`, entao o `.map()` do chamador nao casa
     * nada e o PUT sai com a lista inteira reescrita, nada zerado, com o snack
     * dizendo "removido" — sucesso num no-op.
     */
    resolveRemovalTarget({ eventos, item }) {
      const original = this.findOriginalEvent({ eventos, item })
      const key = original && original._eventUid != null ? '_eventUid' : '_id'
      const alvo = original ? original[key] : undefined
      if (alvo == null) {
        return null
      }
      const achou = (eventos || []).some((e) => e && e[key] === alvo)
      return achou ? { key, alvo } : null
    },
    async removeEvent(item) {
      const eventos = this.resolveEventList(item)
      const alvoRemocao = this.resolveRemovalTarget({ eventos, item })
      if (!alvoRemocao) {
        this.$showSnack({
          status: 400,
          message:
            'Não foi possível identificar o evento para remover. Recarregue a página e tente de novo.',
        })
        return
      }
      const { key, alvo } = alvoRemocao
      const lista = normalizeEventList(
        (eventos || []).map((e) =>
          e && e[key] === alvo ? zeroEventValues(e) : e
        )
      )
      const idAtivo = (item && item.id_ativo) || (this.asset && this.asset.id)
      const tipo = item && item.split ? EVENT_TYPE.SPLITS : EVENT_TYPE.PROVENTOS

      const r = await apiConsolidatorEvents.salvar({
        id_ativo: idAtivo,
        lista,
        tipo,
      })
      if (r && r.status === 200) {
        GlobalBus.$emit(RELOAD.CONSOLIDATOR_ASSET_EVENT, true)
      }
      this.$showSnack(r)
    },
    findOriginalEvent({ eventos, item }) {
      if (!item) {
        return item
      }
      const key = item._eventUid == null ? '_id' : '_eventUid'
      if (item[key] == null) {
        return item
      }
      const found = (eventos || []).find((e) => e && e[key] === item[key])
      return found || item
    },
    resolveEventList(item) {
      const source = item && item.split ? this.splits : this.proventos
      if (Array.isArray(source)) {
        return source
      }
      if (source && item) {
        return source[item.id_ativo] || []
      }
      return []
    },
  },
}
</script>

<template>
  <div class="movements-events-table">
    <MovimentacoesTable
      v-if="mode === 'movements'"
      :sugestoes-por-movimentacao="sugestoesPorMovimentacao"
      @revisar-sugestoes="$emit('revisar-sugestoes', $event)"
      :title="resolvedTitle"
      :asset="asset"
      :search="search"
      :items="movementItems"
      :loading="loading"
      :show-alert-fill-pu="showAlertFillPu"
      :id-asset-alert-fill-pu="idAssetAlertFillPu"
      :show-asset-name-column="showAssetNameColumn"
      :read-only="readOnly"
      @open-movimentacoes="$emit('open-movimentacoes', $event)"
      @edit-aporte-resgate="onEditMovement"
      @remove-movimentacao="onRemoveMovement"
    />

    <EventosTable
      v-else-if="mode === 'events'"
      :title="resolvedTitle"
      :asset="asset"
      :search="search"
      :items="eventItems"
      :loading="loading"
      :show-asset-name-column="showAssetNameColumn"
      :read-only="readOnly"
      @edit-event="onEditEvent"
      @remove-event="onRemoveEvent"
    >
      <template v-slot:cornerAction>
        <slot name="cornerAction" />
      </template>
    </EventosTable>

    <MovementsEventsCombinedTable
      v-else
      :sugestoes-por-movimentacao="sugestoesPorMovimentacao"
      @revisar-sugestoes="$emit('revisar-sugestoes', $event)"
      :title="resolvedTitle"
      :asset="asset"
      :movement-items="movementItems || []"
      :event-items="eventItems || []"
      :search="search"
      :loading="loading"
      :show-alert-fill-pu="showAlertFillPu"
      :id-asset-alert-fill-pu="idAssetAlertFillPu"
      :show-asset-name-column="showAssetNameColumn"
      :read-only="readOnly"
      @edit-movement="onEditMovement"
      @remove-movement="onRemoveMovement"
      @edit-event="onEditEvent"
      @remove-event="onRemoveEvent"
    />
  </div>
</template>

<style lang="sass">
.movements-events-table
  width: 100%
</style>
